import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import HistoryIcon from "@mui/icons-material/History";
import InfoIcon from "@mui/icons-material/Info";
import { posInputFocus } from "../../../../../store/pos/posSlice";
import InputText from "../../../../../components/inputs/InputText";
import { toast } from "react-toastify";
import "./PosModel.scss";
import posApiService, {
  PosStatus,
  PosHistoryItem,
} from "../../../../../services/pos/posApiService";
import { getDecryptedToken } from "../../../../../utils/crypto/cryptoUtils";
import { getCurrencySymbol } from "../../../../../utils/currency/currencyUtils";
import CashManagementPrint from "./CashManagementPrint";
import { Session } from "inspector";
import useSubmitting from "../../../../../hooks/useSubmitting";

// Map currency symbols to time zones
const currencyTimeZoneMap: Record<string, string> = {
  $: "America/New_York", // US Dollar
  "¥": "Asia/Tokyo", // Japanese Yen (default to Tokyo)
  "Rs.": "Asia/Colombo", // Sri Lankan Rupee
  "€": "Europe/Berlin", // Euro
  "£": "Europe/London", // British Pound
  "₹": "Asia/Kolkata", // Indian Rupee
  A$: "Australia/Sydney", // Australian Dollar
  C$: "America/Toronto", // Canadian Dollar
  // Add more as needed
};

const PosModel = ({
  isCloseMain,
  isOpen,
}: {
  isCloseMain: () => void;
  isOpen: boolean;
}) => {
  const [formData, setFormData] = useState({
    amount: "",
    reason: "",
    operationType: "cashIn", // 'cashIn' or 'cashOut'
  });

  const [submitting, wrap] = useSubmitting();
  const [posStatus, setPosStatus] = useState<PosStatus | null>(null);
  const [currentView, setCurrentView] = useState<"form" | "status" | "history">(
    "form",
  );
  const [posHistory, setPosHistory] = useState<PosHistoryItem[]>([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  console.log("POS History:", posHistory);

  // New state for cash discrepancy popup
  const [showClosePopup, setShowClosePopup] = useState(false);
  const [closeSessionData, setCloseSessionData] = useState({
    actualCashAmount: "",
    discrepancyReason: "",
  });
  const [closeSessionLoading, setCloseSessionLoading] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);

  // Live time logic (date and time only)
  const [currentTime, setCurrentTime] = useState(new Date());

  // Get currency from token
  const currency = getDecryptedToken("currency");

  // Pick time zone based on currency symbol, fallback to browser time zone
  const timeZone =
    currencyTimeZoneMap[currency as string] ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  const dispatch = useDispatch();

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        amount: "",
        reason: "",
        operationType: "cashIn",
      });
      setCurrentView("form");
    } else {
      // Load POS status when modal opens
      loadPosStatus();
    }
  }, [isOpen]);

  const loadPosStatus = async () => {
    setStatusLoading(true);
    try {
      const result = await posApiService.getPosStatus();
      if (result.success) {
        setPosStatus(result.data);
      }
    } catch (error: any) {
      console.error("Error loading POS status:", error);
      toast.error("Failed to load POS status");
    } finally {
      setStatusLoading(false);
    }
  };

  const loadPosHistory = async () => {
    setHistoryLoading(true);
    try {
      const result = await posApiService.getPosHistory();
      if (result.success) {
        setPosHistory(result.data || []);
      }
    } catch (error: any) {
      console.error("Error loading POS history:", error);
      toast.error("Failed to load POS history");
      setPosHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOperationTypeChange = (type: string) => {
    setFormData((prev) => ({
      ...prev,
      operationType: type,
      amount: "",
      reason: "",
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const drawerId = getDecryptedToken("selectedDrawer");

    if (!formData.amount || Number(formData.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      const payload = {
        amount: Number(formData.amount),
        reason: formData?.reason?.trim(),
        drawerId: Number(drawerId),
        sessionId: "sample",
      };

      let result;
      if (formData.operationType === "cashIn") {
        result = await posApiService.cashIn(payload);
      } else {
        result = await posApiService.cashOut(payload);
      }

      if (result.success) {
        toast.success(
          `${
            formData.operationType === "cashIn" ? "Cash In" : "Cash Out"
          } recorded successfully!`,
        );

        // Reset form
        setFormData({
          amount: "",
          reason: "",
          operationType: "cashIn",
        });

        // Refresh POS status
        await loadPosStatus();

        // Show success message with new balance
        if (result.data?.newBalance !== undefined) {
          const currencySymbol = getCurrencySymbol();
          toast.info(
            `New balance: ${currencySymbol}${Number(result.data.newBalance).toFixed(2)}`,
          );
        }
      } else {
        toast.error(result.message || "Operation failed");
      }
    } catch (error: any) {
      console.error("Cash operation error:", error);

      toast.error(error.message || "Failed to record cash operation");
    }
  };

  const handleCloseSession = async () => {
    if (!posStatus?.isOpen) {
      toast.error("No open POS session to close");
      return;
    }

    // Show the cash counting popup instead of directly closing
    setShowClosePopup(true);
    setCloseSessionData({
      actualCashAmount: Number(posStatus.currentBalance).toFixed(2),
      discrepancyReason: "",
    });
  };

  const handleCloseSessionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!closeSessionData.actualCashAmount) {
      toast.error("Please enter the actual cash amount");
      return;
    }

    const discrepancy =
      Number(closeSessionData.actualCashAmount) -
      (posStatus?.currentBalance || 0);

    if (discrepancy !== 0 && !closeSessionData.discrepancyReason.trim()) {
      toast.error("Please provide a reason for the cash discrepancy");
      return;
    }

    setCloseSessionLoading(true);

    try {
      const result = await posApiService.closePosSession({
        actualCashAmount: Number(closeSessionData.actualCashAmount),
        discrepancyReason: closeSessionData.discrepancyReason.trim(),
      });

      if (result.success) {
        const data = result.data;

        if (data && data.adjustmentMade) {
          const sym = getCurrencySymbol();
          let adjustmentMessage = "";
          if (data.adjustmentType === "cash_in_adjustment") {
            adjustmentMessage = `Cash In adjusted by +${sym}${Number(data.adjustmentAmount).toFixed(2)} (More cash found than expected)`;
          } else if (data.adjustmentType === "cash_out_adjustment") {
            adjustmentMessage = `Cash Out adjusted by +${sym}${Number(data.adjustmentAmount).toFixed(2)} (Less cash found than expected)`;
          }

          toast.info(adjustmentMessage, { autoClose: 8000 });
        }

        toast.success("POS session closed successfully");

        if (
          data &&
          typeof data.discrepancy === "number" &&
          data.discrepancy !== 0
        ) {
          const currencySymbol = getCurrencySymbol();
          toast.warning(
            `Cash discrepancy: ${
              data.discrepancy > 0 ? "+" : ""
            }${currencySymbol}${Number(data.discrepancy).toFixed(2)} - Automatically adjusted`,
            { autoClose: 6000 },
          );
        }

        setShowClosePopup(false);
        await loadPosStatus();
      }
    } catch (error: any) {
      console.error("Error closing session:", error);
      toast.error("Failed to close POS session");
    } finally {
      setCloseSessionLoading(false);
    }
  };

  const handleClosePopupInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = e.target;
    setCloseSessionData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle click outside modal to close
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      isCloseMain();
    }
  };

  if (!isOpen) return null;

  // Currency symbol for displaying amounts
  const currencySymbol = getCurrencySymbol();

  // Helper function to format datetime with timezone
  const formatDateTime = (date: Date | string) => {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return (
      dateObj.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        timeZone,
      }) +
      " " +
      dateObj.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
        timeZone,
      })
    );
  };

  return (
    <div className="pos_modal_overlay" onClick={handleOverlayClick}>
      <div className="pos_modal_container">
        {/* Modal Header */}
        <div className="pos_modal_header">
          <h2 className="pos_modal_title">Cash Management</h2>
          <button className="pos_modal_close" onClick={isCloseMain}>
            <CloseIcon />
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="pos_modal_nav">
          <button
            type="button"
            className={`nav_btn ${currentView === "form" ? "active" : ""}`}
            onClick={() => setCurrentView("form")}
          >
            <AccountBalanceWalletIcon />
            Cash Operations
          </button>
          <button
            type="button"
            className={`nav_btn ${currentView === "status" ? "active" : ""}`}
            onClick={() => {
              setCurrentView("status");
              loadPosStatus();
            }}
          >
            <InfoIcon />
            Current Status
          </button>
          <button
            type="button"
            className={`nav_btn ${currentView === "history" ? "active" : ""}`}
            onClick={() => {
              setCurrentView("history");
              loadPosHistory();
            }}
          >
            <HistoryIcon />
            History
          </button>
        </div>

        {/* Modal Body */}
        <div className="pos_modal_body">
          {currentView === "form" && (
            <>
              {/* Operation Type Selector */}
              <div className="operation_type_selector">
                <button
                  type="button"
                  className={`operation_btn ${
                    formData.operationType === "cashIn" ? "active" : ""
                  }`}
                  onClick={() => handleOperationTypeChange("cashIn")}
                >
                  <AddCircleOutlineIcon />
                  Cash In
                </button>
                <button
                  type="button"
                  className={`operation_btn ${
                    formData.operationType === "cashOut" ? "active" : ""
                  }`}
                  onClick={() => handleOperationTypeChange("cashOut")}
                >
                  <RemoveCircleOutlineIcon />
                  Cash Out
                </button>
              </div>

              {/* Form */}
              <form onSubmit={wrap(handleSubmit)} className="pos_modal_form">
                <div className="form_grid">
                  {formData.operationType === "cashIn" ? (
                    <div className="form_group">
                      <InputText
                        required={true}
                        name="amount"
                        label="Cash In Amount"
                        type="number"
                        placeholder="Enter amount"
                        value={formData.amount}
                        onChange={handleInputChange}
                        onFocus={(e) => {
                          dispatch(
                            posInputFocus({
                              name: "data",
                              value: e.target.name,
                            }),
                          );
                        }}
                      />
                    </div>
                  ) : (
                    <div className="form_group">
                      <InputText
                        name="amount"
                        label="Cash Out Amount"
                        type="number"
                        placeholder="Enter amount"
                        value={formData.amount}
                        onChange={handleInputChange}
                        onFocus={(e) => {
                          dispatch(
                            posInputFocus({
                              name: "data",
                              value: e.target.name,
                            }),
                          );
                        }}
                      />
                    </div>
                  )}

                  <div className="form_group">
                    <InputText
                      name="reason"
                      label="Reason"
                      type="text"
                      placeholder="Enter reason for this operation"
                      value={formData.reason}
                      onChange={handleInputChange}
                      onFocus={(e) => {
                        dispatch(
                          posInputFocus({ name: "data", value: e.target.name }),
                        );
                      }}
                    />
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="pos_modal_footer">
                  <button
                    type="button"
                    className="btn_cancel"
                    onClick={isCloseMain}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn_submit"
                    disabled={submitting}
                  >
                    {submitting
                      ? "Processing..."
                      : `Record ${
                          formData.operationType === "cashIn"
                            ? "Cash In"
                            : "Cash Out"
                        }`}
                  </button>
                </div>
              </form>
            </>
          )}

          {currentView === "status" && (
            <div className="pos_status_view">
              {statusLoading ? (
                <div className="loading_state">Loading POS status...</div>
              ) : posStatus ? (
                <div className="status_content">
                  <div className="status_card">
                    <h3>Current POS Session</h3>
                    <div className="status_grid">
                      <div className="status_item">
                        <label>Status:</label>
                        <span
                          className={`status_badge ${
                            posStatus.isOpen ? "open" : "closed"
                          }`}
                        >
                          {posStatus.isOpen ? "Open" : "Closed"}
                        </span>
                      </div>
                      {posStatus.isOpen && (
                        <>
                          <div className="status_item">
                            <label>Cash In (Received):</label>
                            <span className="amount positive">
                              {currencySymbol}
                              {Number(posStatus.cashIn).toFixed(2)}
                            </span>
                          </div>
                          <div className="status_item">
                            <label>Cash Out (Withdrawn):</label>
                            <span className="amount negative">
                              {currencySymbol}
                              {Number(posStatus.cashOut).toFixed(2)}
                            </span>
                          </div>
                          <div className="status_item">
                            <label>Current Drawer Balance:</label>
                            <span
                              className={`amount ${
                                posStatus.currentBalance >= 0
                                  ? "positive"
                                  : "negative"
                              }`}
                            >
                              {currencySymbol}
                              {Number(posStatus.currentBalance).toFixed(2)}
                            </span>
                          </div>
                          <div className="status_item full-width">
                            <label>Session Period:</label>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px",
                              }}
                            >
                              <span>
                                Start:{" "}
                                {posHistory.length > 0
                                  ? formatDateTime(
                                      posHistory[posHistory.length - 1]
                                        .createdAt || "",
                                    )
                                  : "N/A"}
                              </span>
                              <span>End: {formatDateTime(currentTime)}</span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    {posStatus.isOpen && (
                      <div className="status_actions">
                        <button
                          type="button"
                          className="btn_close_session"
                          onClick={handleCloseSession}
                        >
                          Close Registry
                        </button>

                        {/* New print button beside Close Session */}
                        <button
                          type="button"
                          className="btn_print_cash"
                          onClick={() => {
                            // ensure latest history loaded for details
                            loadPosHistory().catch(() => {});
                            setPrintOpen(true);
                          }}
                        >
                          Print Cash Mgmt
                        </button>
                      </div>
                    )}
                  </div>

                  {posStatus.isOpen && (
                    <div className="cash_flow_info">
                      <h4>Cash Flow Information</h4>
                      <p>
                        <strong>Note:</strong> Cash balance includes all cash
                        transactions:
                      </p>
                      <ul>
                        <li>
                          • Cash received from customers increases the balance
                        </li>
                        <li>
                          • Change given to customers decreases the balance
                        </li>
                        <li>
                          • Manual cash in/out operations affect the balance
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="empty_state">
                  <p>No POS session data available</p>
                </div>
              )}
            </div>
          )}

          {currentView === "history" && (
            <div className="pos_history_view">
              {historyLoading ? (
                <div className="loading_state">Loading history...</div>
              ) : posHistory.length > 0 ? (
                <div className="history_content">
                  <h3>Cash Operations History</h3>
                  <div className="history_list">
                    {posHistory.map((item) => (
                      <div key={item.id} className="history_item">
                        <div className="history_main">
                          <div className="history_amount">
                            <span className="amount positive">
                              +{currencySymbol}
                              {Number(item.cashIn).toFixed(2)}
                            </span>
                            <span className="amount negative">
                              -{currencySymbol}
                              {Number(item.cashOut).toFixed(2)}
                            </span>
                          </div>
                          <div className="history_details">
                            <span className="reason">
                              {item.reason || "No reason provided"}
                            </span>
                            <span className="date">
                              {formatDateTime(item.createdAt)}
                            </span>
                          </div>
                        </div>
                        <div className="history_status">
                          {!item.active ? (
                            <span className="status_badge closed">Closed</span>
                          ) : (
                            <span className="status_badge open">Open</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="empty_state">
                  <p>No transaction history available</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Cash Discrepancy Popup */}
      {showClosePopup && (
        <div className="pos_modal_overlay">
          <div className="pos_modal_container close_session_popup">
            <div className="pos_modal_header">
              <h2 className="pos_modal_title">Close POS Session</h2>
              <button
                className="pos_modal_close"
                onClick={() => setShowClosePopup(false)}
                disabled={closeSessionLoading}
              >
                <CloseIcon />
              </button>
            </div>

            <div className="pos_modal_body">
              <div className="close_session_content">
                <div className="expected_balance_info">
                  <h3>
                    Expected Cash Balance: {currencySymbol}
                    {Number(posStatus?.currentBalance).toFixed(2)}
                  </h3>
                  <p>
                    Please count the actual cash in the drawer and enter the
                    amount below:
                  </p>
                </div>

                <form
                  onSubmit={handleCloseSessionSubmit}
                  className="close_session_form"
                >
                  <div className="form_group">
                    <InputText
                      required={true}
                      name="actualCashAmount"
                      label="Actual Cash Amount in Drawer"
                      type="number"
                      placeholder="Count and enter actual cash amount"
                      value={closeSessionData.actualCashAmount}
                      onChange={handleClosePopupInputChange}
                    />
                  </div>

                  <div className="form_group">
                    <InputText
                      required={
                        Number(closeSessionData.actualCashAmount) !==
                        (posStatus?.currentBalance || 0)
                      }
                      name="discrepancyReason"
                      label={`Reason for Discrepancy ${
                        Number(closeSessionData.actualCashAmount) !==
                        (posStatus?.currentBalance || 0)
                          ? "(Required)"
                          : "(Optional)"
                      }`}
                      type="text"
                      placeholder="Explain the reason for cash difference"
                      value={closeSessionData.discrepancyReason}
                      onChange={handleClosePopupInputChange}
                    />
                  </div>

                  {closeSessionData.actualCashAmount && (
                    <div className="discrepancy_preview">
                      <div
                        className={`discrepancy_amount ${
                          Number(closeSessionData.actualCashAmount) -
                            (posStatus?.currentBalance || 0) ===
                          0
                            ? "balanced"
                            : Number(closeSessionData.actualCashAmount) -
                                  (posStatus?.currentBalance || 0) >
                                0
                              ? "positive"
                              : "negative"
                        }`}
                      >
                        Discrepancy: {currencySymbol}
                        {(
                          Number(closeSessionData.actualCashAmount) -
                          (posStatus?.currentBalance || 0)
                        ).toFixed(2)}
                      </div>

                      {Number(closeSessionData.actualCashAmount) !==
                        (posStatus?.currentBalance || 0) && (
                        <div className="adjustment_info">
                          <p className="adjustment_text">
                            {Number(closeSessionData.actualCashAmount) >
                            (posStatus?.currentBalance || 0)
                              ? `System will add ${currencySymbol}${(
                                  Number(closeSessionData.actualCashAmount) -
                                  (posStatus?.currentBalance || 0)
                                ).toFixed(2)} to Cash In (surplus found)`
                              : `System will add ${currencySymbol}${(
                                  (posStatus?.currentBalance || 0) -
                                  Number(closeSessionData.actualCashAmount)
                                ).toFixed(2)} to Cash Out (deficit found)`}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="pos_modal_footer">
                    <button
                      type="button"
                      className="btn_cancel"
                      onClick={() => setShowClosePopup(false)}
                      disabled={closeSessionLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn_submit"
                      disabled={closeSessionLoading}
                    >
                      {closeSessionLoading
                        ? "Closing..."
                        : "Close Session & Adjust"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cash Management Print Popup */}
      <CashManagementPrint
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        posStatus={posStatus}
        posHistory={posHistory}
      />
    </div>
  );
};

export default PosModel;
